/**
 * 工作流进度区块组件
 * 整合阶段进度显示和操作
 */

import { CheckCircle, GitBranch } from 'lucide-react'
import { useState } from 'react'

import {
  useSubmitForReview,
  useApproveWorkflow,
  useStartTrack,
  useSubmitTrackForReview,
  useApproveTrack,
  useRejectTrack,
  useRollbackFromTesting,
  type WorkflowConfig,
  type TimeConfigResult,
  type StageEstimateResult,
  type TeamStageOwners,
} from '@/hooks'
import { cn } from '@/lib/utils'

import { WorkflowActions } from '../actions/inline-action'
import { TrackActions } from '../actions/track-actions'
import { RejectTrackDialog, ApproveDialog } from '../dialogs/reject-track-dialog'
import { TrackRollbackDialog } from '../dialogs/rollback-dialog'
import { StartTrackDialog } from '../dialogs/start-track-dialog'
import { StageNode } from '../progress/stage-node'
import { TrackCard } from '../progress/track-node'
import { type TimeStatus } from '../workflow-constants'
import {
  getEnabledStages,
  getCurrentStageIndex,
  isInParallelDevelopment,
  canRollbackTracks,
  isTrackDone,
} from '../workflow-utils'

import type { WorkflowStage, TrackType, TrackStatus } from '@company/shared'

// ==================== 类型定义 ====================

interface WorkflowProgressSectionProps {
  issueId: string
  config: WorkflowConfig
  timeConfig?: TimeConfigResult | null
  teamId?: string
  stageOwners?: TeamStageOwners | null
  getEmployeeName?: (id: string | null) => string | null
  onShowReject: () => void
  className?: string
}

// ==================== 组件实现 ====================

export function WorkflowProgressSection({
  issueId,
  config,
  timeConfig,
  teamId,
  stageOwners,
  getEmployeeName,
  onShowReject,
  className,
}: WorkflowProgressSectionProps) {
  const enabledStages = getEnabledStages(config)
  const currentIndex = getCurrentStageIndex(config, enabledStages)
  const isInParallelDev = isInParallelDevelopment(config.currentStage)

  // Mutations
  const submitMutation = useSubmitForReview()
  const approveMutation = useApproveWorkflow()
  const rollbackMutation = useRollbackFromTesting()
  const startTrackMutation = useStartTrack()
  const submitTrackMutation = useSubmitTrackForReview()
  const approveTrackMutation = useApproveTrack()
  const rejectTrackMutation = useRejectTrack()

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRollbackDialog, setShowRollbackDialog] = useState(false)
  const [startTrackType, setStartTrackType] = useState<TrackType | null>(null)
  const [rejectTrackType, setRejectTrackType] = useState<TrackType | null>(null)

  // 获取阶段的时间状态
  const getStageTimeStatus = (stage: WorkflowStage): TimeStatus | null => {
    if (!timeConfig) {
      return null
    }
    const estimate = timeConfig.stageEstimates.find((e: StageEstimateResult) => e.stage === stage)
    return (estimate?.timeStatus as TimeStatus) ?? null
  }

  // 获取阶段负责人信息
  const getStageOwnerName = (stage: WorkflowStage): string | null => {
    const reviewers = config.stageReviewers

    switch (stage) {
      case 'product_discussion':
        return reviewers.productDiscussion?.name ?? null
      case 'ui_design':
        if (stageOwners?.uiDesignOwnerId && getEmployeeName) {
          return getEmployeeName(stageOwners.uiDesignOwnerId)
        }
        return reviewers.uiDesign?.name ?? null
      case 'ui_confirmation':
        return reviewers.uiConfirmation?.name ?? null
      case 'frontend_development':
        if (stageOwners?.frontendOwnerId && getEmployeeName) {
          return getEmployeeName(stageOwners.frontendOwnerId)
        }
        return null
      case 'backend_development':
        if (stageOwners?.backendOwnerId && getEmployeeName) {
          return getEmployeeName(stageOwners.backendOwnerId)
        }
        return null
      case 'code_review':
        return reviewers.codeReview?.name ?? null
      case 'ops_deployment':
        return reviewers.opsDeployment?.name ?? null
      case 'testing':
        if (stageOwners?.testingOwnerId && getEmployeeName) {
          return getEmployeeName(stageOwners.testingOwnerId)
        }
        return reviewers.testing?.name ?? null
      case 'product_acceptance':
        return reviewers.productAcceptance?.name ?? null
      default:
        return null
    }
  }

  // 轨道操作处理
  const handleStartTrack = (track: TrackType, assigneeId: string) => {
    startTrackMutation.mutate(
      { issueId, track, data: { assigneeId } },
      { onSuccess: () => setStartTrackType(null) }
    )
  }

  const handleSubmitTrack = (track: TrackType) => {
    submitTrackMutation.mutate({ issueId, track })
  }

  const handleApproveTrack = (track: TrackType) => {
    approveTrackMutation.mutate({ issueId, track })
  }

  const handleRejectTrack = (track: TrackType, reason: string) => {
    rejectTrackMutation.mutate(
      { issueId, track, data: { reason } },
      { onSuccess: () => setRejectTrackType(null) }
    )
  }

  const handleRollbackTracks = (tracks: TrackType[], reason: string) => {
    rollbackMutation.mutate(
      { issueId, data: { tracks, reason } },
      { onSuccess: () => setShowRollbackDialog(false) }
    )
  }

  // 阶段操作处理
  const handleSubmit = () => submitMutation.mutate(issueId)
  const handleApprove = () => {
    approveMutation.mutate(issueId, {
      onSuccess: () => setShowApproveDialog(false),
    })
  }

  // 是否可以精准回退轨道
  const showTrackRollback = canRollbackTracks(config)

  // 渲染轨道操作
  const renderTrackActions = (track: TrackType, status: TrackStatus) => (
    <TrackActions
      track={track}
      status={status}
      onStart={() => setStartTrackType(track)}
      onSubmit={() => handleSubmitTrack(track)}
      onApprove={() => handleApproveTrack(track)}
      onReject={() => setRejectTrackType(track)}
      startLoading={startTrackMutation.isPending}
      submitLoading={submitTrackMutation.isPending}
      approveLoading={approveTrackMutation.isPending}
    />
  )

  // 获取错误信息
  const errorMessage =
    submitMutation.error?.message ||
    approveMutation.error?.message ||
    rollbackMutation.error?.message ||
    startTrackMutation.error?.message ||
    submitTrackMutation.error?.message ||
    approveTrackMutation.error?.message ||
    rejectTrackMutation.error?.message

  return (
    <div className={cn('bg-card rounded-lg border p-4', className)}>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-medium">工作流进度</h4>
        {timeConfig && (
          <span className="text-muted-foreground text-xs">
            总预估: {timeConfig.totalEstimatedHours.toFixed(1)}h
          </span>
        )}
      </div>

      <div className="space-y-4">
        {enabledStages.map((stage, index) => {
          // 并行开发阶段特殊处理
          if (stage === 'parallel_development') {
            const frontendDone = isTrackDone(config.frontendTrackStatus)
            const backendDone = isTrackDone(config.backendTrackStatus)
            const allDone = frontendDone && backendDone
            const frontendSkipped = config.frontendTrackStatus === 'skipped'
            const backendSkipped = config.backendTrackStatus === 'skipped'

            return (
              <div key={stage} className="relative">
                {/* 阶段标题 */}
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full',
                      allDone &&
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                      isInParallelDev &&
                        !allDone &&
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                      !isInParallelDev && !allDone && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {allDone ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <GitBranch className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-base font-medium',
                        allDone && 'text-green-700 dark:text-green-400',
                        isInParallelDev && !allDone && 'text-foreground',
                        !isInParallelDev && !allDone && 'text-muted-foreground'
                      )}
                    >
                      开发 & 代码审查
                    </span>
                    {isInParallelDev && !allDone && (
                      <span className="rounded-full bg-blue-500 px-2.5 py-1 text-xs font-medium text-white">
                        当前阶段
                      </span>
                    )}
                  </div>
                </div>

                {/* 轨道卡片 - 并排布局 */}
                <div className="ml-4 grid gap-3 sm:grid-cols-2">
                  {/* 前端轨道卡片 */}
                  {!frontendSkipped && (
                    <TrackCard
                      track="frontend"
                      status={config.frontendTrackStatus}
                      startedAt={config.frontendStartedAt}
                      submittedAt={config.frontendSubmittedAt}
                      approvedAt={config.frontendApprovedAt}
                      actions={
                        isInParallelDev
                          ? renderTrackActions('frontend', config.frontendTrackStatus)
                          : undefined
                      }
                    />
                  )}

                  {/* 后端轨道卡片 */}
                  {!backendSkipped && (
                    <TrackCard
                      track="backend"
                      status={config.backendTrackStatus}
                      startedAt={config.backendStartedAt}
                      submittedAt={config.backendSubmittedAt}
                      approvedAt={config.backendApprovedAt}
                      actions={
                        isInParallelDev
                          ? renderTrackActions('backend', config.backendTrackStatus)
                          : undefined
                      }
                    />
                  )}
                </div>

                {/* 轨道回退按钮 - 放在卡片下方 */}
                {isInParallelDev && showTrackRollback && (
                  <div className="ml-4 mt-3">
                    <button
                      onClick={() => setShowRollbackDialog(true)}
                      className="text-sm text-orange-600 hover:text-orange-700 hover:underline dark:text-orange-400 dark:hover:text-orange-300"
                    >
                      回退轨道
                    </button>
                  </div>
                )}
              </div>
            )
          }

          // 普通阶段
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex
          const status = isCompleted ? 'completed' : isCurrent ? 'current' : 'pending'
          const timeStatus = getStageTimeStatus(stage)
          const ownerName = getStageOwnerName(stage)

          return (
            <StageNode
              key={stage}
              stage={stage}
              index={index}
              status={status}
              timeStatus={timeStatus}
              ownerName={ownerName}
              startedAt={isCurrent ? config.currentStageStartedAt : undefined}
              isLast={index === enabledStages.length - 1}
              actions={
                isCurrent && config.currentStage !== 'done' ? (
                  <WorkflowActions
                    onSubmit={handleSubmit}
                    onApprove={() => setShowApproveDialog(true)}
                    onReject={showTrackRollback ? undefined : onShowReject}
                    onRollback={showTrackRollback ? () => setShowRollbackDialog(true) : undefined}
                    submitLoading={submitMutation.isPending}
                    approveLoading={approveMutation.isPending}
                    showRollback={showTrackRollback}
                    showReject={!showTrackRollback}
                  />
                ) : undefined
              }
            />
          )
        })}
      </div>

      {/* 任务已完成提示 */}
      {config.currentStage === 'done' && (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
          <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
            <CheckCircle className="h-4 w-4" />
            <span>任务已完成</span>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {errorMessage && (
        <div className="bg-destructive/10 text-destructive mt-3 rounded-md p-2 text-xs">
          {errorMessage}
        </div>
      )}

      {/* 对话框 */}
      <ApproveDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        onConfirm={handleApprove}
        loading={approveMutation.isPending}
        error={approveMutation.error?.message}
      />

      <TrackRollbackDialog
        open={showRollbackDialog}
        onOpenChange={setShowRollbackDialog}
        frontendEnabled={config.frontendTrackStatus !== 'skipped'}
        backendEnabled={config.backendTrackStatus !== 'skipped'}
        onConfirm={handleRollbackTracks}
        loading={rollbackMutation.isPending}
        error={rollbackMutation.error?.message}
      />

      {startTrackType && (
        <StartTrackDialog
          open={!!startTrackType}
          onOpenChange={(open) => !open && setStartTrackType(null)}
          track={startTrackType}
          teamId={teamId}
          onConfirm={(assigneeId) => handleStartTrack(startTrackType, assigneeId)}
          loading={startTrackMutation.isPending}
          error={startTrackMutation.error?.message}
        />
      )}

      {rejectTrackType && (
        <RejectTrackDialog
          open={!!rejectTrackType}
          onOpenChange={(open) => !open && setRejectTrackType(null)}
          track={rejectTrackType}
          onConfirm={(reason) => handleRejectTrack(rejectTrackType, reason)}
          loading={rejectTrackMutation.isPending}
          error={rejectTrackMutation.error?.message}
        />
      )}
    </div>
  )
}
